import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, Trash2, Download, FileText, Filter } from "lucide-react";

export const AdminDocuments = () => {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  useEffect(() => {
    fetchDocuments();
  }, [token, filterStatus]);
  
  const fetchDocuments = async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const response = await axios.get(`${API}/admin/documents${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  };
  
  const updateStatus = async (docId, newStatus) => {
    try {
      await axios.put(`${API}/admin/documents/${docId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Statut mis à jour");
      fetchDocuments();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };
  
  const handleDelete = async (docId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce document ?")) return;
    
    try {
      await axios.delete(`${API}/admin/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document supprimé");
      fetchDocuments();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };
  
  const downloadDocument = (doc) => {
    // Create blob from base64 and download
    const byteCharacters = atob(doc.file_data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: doc.file_type });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = doc.file_name;
    link.click();
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      reviewed: "bg-blue-100 text-blue-800 border-blue-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200"
    };
    const labels = {
      pending: "En attente",
      reviewed: "Examiné",
      approved: "Approuvé",
      rejected: "Refusé"
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };
  
  const getDocumentTypeLabel = (type) => {
    const labels = {
      identite: "Pièce d'identité",
      revenus: "Justificatifs de revenus",
      bancaire: "Relevés bancaires",
      avis_imposition: "Avis d'imposition",
      contrat_travail: "Contrat de travail",
      quittances_loyer: "3 dernières quittances de loyer",
      promesse_embauche: "Promesse d'embauche",
      taxe_fonciere: "Taxe foncière",
      attestation_hebergement: "Attestation d'hébergement",
      attestation_apl: "Attestation APL",
      attestation_caf: "Attestation allocations familiales",
      autre: "Autre"
    };
    return labels[type] || type;
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-documents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl text-[#0A0A0A]">Documents reçus</h1>
            <p className="text-gray-500 mt-1">{documents.length} document(s)</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-500" />
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="reviewed">Examiné</SelectItem>
                <SelectItem value="approved">Approuvé</SelectItem>
                <SelectItem value="rejected">Refusé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Documents Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Type de document</TableHead>
                <TableHead>Référence du bien</TableHead>
                <TableHead>Fichier</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                    Aucun document reçu
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{doc.client_name}</p>
                        <p className="text-sm text-gray-500">{doc.client_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getDocumentTypeLabel(doc.document_type)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" data-testid={`document-reference-${doc.id}`}>
                        {doc.purpose === 'achat' ? 'Achat' : doc.purpose === 'location' ? 'Location' : (doc.purpose || '—')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-gray-400" />
                        <span className="text-sm truncate max-w-[150px]">{doc.file_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatDate(doc.created_at)}
                    </TableCell>
                    <TableCell>{getStatusBadge(doc.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedDoc(doc);
                            setDialogOpen(true);
                          }}
                          data-testid={`view-document-${doc.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => downloadDocument(doc)}
                          data-testid={`download-document-${doc.id}`}
                        >
                          <Download size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(doc.id)}
                          data-testid={`delete-document-${doc.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Document Detail Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Détails du document</DialogTitle>
            </DialogHeader>
            
            {selectedDoc && (
              <div className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Client</p>
                    <p className="font-medium">{selectedDoc.client_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedDoc.client_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Téléphone</p>
                    <p className="font-medium">{selectedDoc.client_phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Référence du bien</p>
                    <p className="font-medium">{selectedDoc.purpose === 'achat' ? 'Achat immobilier' : selectedDoc.purpose === 'location' ? 'Location' : (selectedDoc.purpose || '—')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Type de document</p>
                    <p className="font-medium">{getDocumentTypeLabel(selectedDoc.document_type)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date de dépôt</p>
                    <p className="font-medium">{formatDate(selectedDoc.created_at)}</p>
                  </div>
                </div>
                
                {selectedDoc.message && (
                  <div>
                    <p className="text-sm text-gray-500">Message du client</p>
                    <p className="mt-1 p-3 bg-gray-50 rounded text-sm">{selectedDoc.message}</p>
                  </div>
                )}
                
                <div>
                  <p className="text-sm text-gray-500 mb-2">Fichier joint</p>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      <FileText size={20} className="text-[#D4AF37]" />
                      <span className="font-medium">{selectedDoc.file_name}</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => downloadDocument(selectedDoc)}>
                      <Download size={16} className="mr-2" />
                      Télécharger
                    </Button>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-2">Statut</p>
                  <Select 
                    value={selectedDoc.status} 
                    onValueChange={(value) => {
                      updateStatus(selectedDoc.id, value);
                      setSelectedDoc({ ...selectedDoc, status: value });
                    }}
                  >
                    <SelectTrigger data-testid="update-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="reviewed">Examiné</SelectItem>
                      <SelectItem value="approved">Approuvé</SelectItem>
                      <SelectItem value="rejected">Refusé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminDocuments;
